import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Upload, AlertCircle, CheckCircle, Database, HardDrive, Zap } from 'lucide-react';
import { DataStore } from '@/lib/data-store';
import { downloadBackup, restoreBackup, getBackupInfo, calculateBackupSize, getLastBackupTime } from '@/lib/backup-utils';
import { toast } from 'sonner';

const DataBackup = () => {
  const [isRestoring, setIsRestoring] = useState(false);
  const [backupInfo, setBackupInfo] = useState('');
  const [showBackupInfo, setShowBackupInfo] = useState(false);

  const handleDownloadBackup = () => {
    try {
      const filename = downloadBackup();
      toast.success(`Backup berhasil diunduh: ${filename}`);
    } catch (error) {
      toast.error('Gagal membuat backup');
      console.error(error);
    }
  };

  const handleRestoreBackup = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsRestoring(true);
    try {
      const success = await restoreBackup(file);
      if (success) {
        toast.success('Data berhasil di-restore');
        window.location.reload();
      } else {
        toast.error('Gagal me-restore data. File tidak valid.');
      }
    } catch (error) {
      toast.error('Error saat me-restore data');
      console.error(error);
    } finally {
      setIsRestoring(false);
    }
  };

  const handleViewBackupInfo = () => {
    const info = getBackupInfo();
    setBackupInfo(info);
    setShowBackupInfo(true);
  };

  const handleClearAllData = () => {
    const confirmed = window.confirm(
      'PERINGATAN: Ini akan menghapus SEMUA data yang ada. Tidak dapat dibatalkan!\n\nYakin ingin lanjut?'
    );
    if (confirmed) {
      const confirmed2 = window.confirm('Tekan OK sekali lagi untuk mengkonfirmasi penghapusan semua data');
      if (confirmed2) {
        DataStore.reset();
        toast.success('Semua data telah dihapus');
        window.location.reload();
      }
    }
  };

  const backupSize = calculateBackupSize();
  const lastBackupTime = getLastBackupTime();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Backup & Restore Data</h1>
        <p className="text-slate-600 mt-1">Kelola backup dan restore data sistem SAP Ngecat</p>
      </div>

      {/* Warning Card */}
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <AlertCircle className="text-yellow-600 flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-yellow-900">Penting: Backup Data Secara Berkala</p>
              <p className="text-sm text-yellow-800 mt-1">
                Lakukan backup data setiap minggu untuk mencegah kehilangan data yang tidak disengaja. 
                Simpan file backup di tempat yang aman.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backup Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">Ukuran Backup</p>
                <HardDrive className="text-blue-600" size={20} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{backupSize}</p>
              <p className="text-xs text-slate-500">Estimated size</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">Status Data</p>
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <p className="text-2xl font-bold text-slate-900">Aktif</p>
              <p className="text-xs text-slate-500">Sistem berjalan normal</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">Terakhir Diperbarui</p>
                <Zap className="text-amber-600" size={20} />
              </div>
              <p className="text-sm font-bold text-slate-900">{lastBackupTime}</p>
              <p className="text-xs text-slate-500">Real-time sync</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Backup Section */}
      <Card>
        <CardHeader>
          <CardTitle>Buat Backup Data</CardTitle>
          <CardDescription>Download seluruh data sistem dalam format JSON</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Backup akan mencakup semua data dari semua modul (Pelanggan, Projek, Invois, dll).
              File akan disimpan dalam format JSON yang dapat di-restore kapan saja.
            </p>
            <div className="flex gap-3">
              <Button onClick={handleDownloadBackup} className="bg-blue-600 hover:bg-blue-700 gap-2 flex-1">
                <Download size={20} />
                Download Backup
              </Button>
              <Button variant="outline" onClick={handleViewBackupInfo} className="flex-1">
                Lihat Info Backup
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Restore Section */}
      <Card>
        <CardHeader>
          <CardTitle>Restore Data dari Backup</CardTitle>
          <CardDescription>Upload file backup untuk mengembalikan data sebelumnya</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Pilih file backup yang sebelumnya telah diunduh. Sistem akan mengganti seluruh data yang ada 
              dengan data dari file backup. Pastikan Anda telah backup data terbaru sebelum melakukan restore.
            </p>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".json"
                onChange={handleRestoreBackup}
                disabled={isRestoring}
                className="hidden"
                id="backup-file"
              />
              <label htmlFor="backup-file" className="cursor-pointer block">
                <Upload className="mx-auto text-slate-400 mb-2" size={32} />
                <p className="font-medium text-slate-900">Pilih file backup JSON</p>
                <p className="text-sm text-slate-600 mt-1">atau drag & drop file di sini</p>
                {isRestoring && <p className="text-sm text-blue-600 mt-2">Sedang me-restore...</p>}
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backup Info Modal */}
      {showBackupInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader className="flex justify-between items-center">
              <CardTitle>Informasi Backup</CardTitle>
              <button
                onClick={() => setShowBackupInfo(false)}
                className="p-2 hover:bg-slate-100 rounded"
              >
                ✕
              </button>
            </CardHeader>
            <CardContent>
              <pre className="bg-slate-100 p-4 rounded overflow-x-auto text-sm">
                {backupInfo}
              </pre>
              <Button
                onClick={() => setShowBackupInfo(false)}
                className="w-full mt-4 bg-slate-600 hover:bg-slate-700"
              >
                Tutup
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Danger Zone */}
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-700">Zona Berbahaya</CardTitle>
          <CardDescription>Tindakan yang tidak dapat dibatalkan</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-red-700">
              Menghapus semua data akan menghilangkan SEMUA informasi yang tersimpan secara permanen.
              Pastikan Anda telah membuat backup sebelum melakukan tindakan ini.
            </p>
            <Button
              onClick={handleClearAllData}
              className="bg-red-600 hover:bg-red-700 w-full gap-2"
            >
              <AlertCircle size={20} />
              Hapus Semua Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Best Practices */}
      <Card>
        <CardHeader>
          <CardTitle>Best Practices untuk Backup</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Buat backup data setiap hari kerja atau minimal seminggu sekali</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Simpan file backup di lebih dari satu lokasi (local + cloud)</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Beri nama file backup dengan tanggal untuk tracking mudah</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Test restore backup secara berkala untuk memastikan file valid</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-sm text-slate-700">Dokumentasikan prosedur backup untuk staff baru</span>
            </li>
            <li className="flex gap-3">
              <span className="text-red-600 font-bold flex-shrink-0">✗</span>
              <span className="text-sm text-slate-700">Jangan menghapus file backup lama tanpa backup terbaru</span>
            </li>
            <li className="flex gap-3">
              <span className="text-red-600 font-bold flex-shrink-0">✗</span>
              <span className="text-sm text-slate-700">Jangan share file backup via email tanpa enkripsi</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataBackup;
